import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Megaphone, Pause, Play } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/cn'
import { lacakPromo, usePromos, type Promo, type PromoAccent, type PromoSlot } from '@/features/promo/api'

/**
 * Warna aksen disimpan sebagai nama, bukan kode heksa, sehingga pemetaannya
 * ada di sini — di tempat tema aplikasi memang didefinisikan. Slide lama
 * ikut berubah ketika temanya berubah, alih-alih membawa warna lama yang
 * bertabrakan dengan tampilan baru.
 */
const aksen: Record<PromoAccent, { blok: string; pil: string; tombol: string }> = {
  hijau: {
    blok: 'bg-brand-500 text-brand-900 hard-dark',
    pil: 'bg-brand-900 text-brand-100',
    tombol: 'bg-arang-900 text-white hover:bg-arang-800',
  },
  ungu: {
    blok: 'bg-ungu-600 text-white',
    pil: 'bg-white/20 text-white',
    tombol: 'bg-white text-ungu-600 hover:bg-kuning-500 hover:text-arang-900',
  },
  kuning: {
    blok: 'bg-kuning-500 text-arang-900 hard-dark',
    pil: 'bg-arang-900 text-kuning-500',
    tombol: 'bg-arang-900 text-white hover:bg-ungu-600',
  },
  gelap: {
    blok: 'bg-arang-900 text-white',
    pil: 'bg-white/15 text-white',
    tombol: 'bg-kuning-500 text-arang-900 hover:bg-brand-500',
  },
}

/**
 * PromoLink membungkus satu tayangan menjadi tautan yang terhitung kliknya.
 *
 * Tautan internal dirender lewat router supaya perpindahannya tetap terasa
 * seketika; tautan luar memakai anchor biasa dengan rel yang menutup akses
 * halaman tujuan ke jendela pembuka.
 */
function PromoLink({
  promo,
  className,
  children,
}: {
  promo: Promo
  className?: string
  children: React.ReactNode
}) {
  const catat = () => lacakPromo(promo.id, 'click')

  if (!promo.target_url) return <div className={className}>{children}</div>

  if (promo.target_url.startsWith('/')) {
    return (
      <Link to={promo.target_url} className={className} onClick={catat}>
        {children}
      </Link>
    )
  }

  return (
    <a
      href={promo.target_url}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
      onClick={catat}
    >
      {children}
    </a>
  )
}

/** LabelSponsor menandai ruang yang dibeli, bukan pilihan redaksi kami. */
function LabelSponsor({ promo, className }: { promo: Promo; className?: string }) {
  if (promo.kind !== 'ad') return null

  return (
    <span className={cn('mono-label text-[10px] opacity-70', className)}>
      Bersponsor{promo.advertiser ? ` · ${promo.advertiser}` : ''}
    </span>
  )
}

const JEDA_SLIDE = 6000

/**
 * PromoSlideshow menayangkan slide beranda secara bergantian.
 *
 * Tidak ada yang dirender bila slidenya kosong atau gagal dimuat: slideshow
 * adalah etalase, dan petak kosong bertuliskan "gagal memuat" di paling atas
 * beranda jauh lebih merusak daripada ketiadaannya.
 */
export function PromoSlideshow({ className }: { className?: string }) {
  const { data } = usePromos('beranda_slideshow')
  const slides = data?.promos ?? []

  const [indeks, setIndeks] = useState(0)
  const [berjalan, setBerjalan] = useState(true)
  const dicatat = useRef(new Set<string>())
  const sentuh = useRef<number | null>(null)

  const jumlah = slides.length
  const ke = useCallback(
    (arah: number) => setIndeks((kini) => (jumlah === 0 ? 0 : (kini + arah + jumlah) % jumlah)),
    [jumlah],
  )

  // Indeks dikembalikan ke awal bila jumlah slidenya menyusut — misalnya
  // ketika satu slide berakhir masa tayangnya saat halaman masih terbuka.
  useEffect(() => {
    setIndeks((kini) => (kini < jumlah ? kini : 0))
  }, [jumlah])

  useEffect(() => {
    if (!berjalan || jumlah < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const timer = window.setInterval(() => ke(1), JEDA_SLIDE)
    return () => window.clearInterval(timer)
  }, [berjalan, jumlah, ke])

  // Satu tayangan dihitung sekali per kunjungan. Tanpa penjaga ini, slide
  // pertama akan terhitung berulang setiap kali putarannya kembali ke awal
  // dan rasio kliknya menyusut sendiri selama halaman dibiarkan terbuka.
  useEffect(() => {
    const slide = slides[indeks]
    if (!slide || dicatat.current.has(slide.id)) return

    dicatat.current.add(slide.id)
    lacakPromo(slide.id, 'view')
  }, [indeks, slides])

  const slide = slides[indeks]
  if (!slide) return null

  const warna = aksen[slide.accent] ?? aksen.hijau

  return (
    <section
      className={cn('relative', className)}
      aria-roledescription="carousel"
      aria-label="Sorotan skava.id"
      onMouseEnter={() => setBerjalan(false)}
      onMouseLeave={() => setBerjalan(true)}
      onTouchStart={(e) => {
        sentuh.current = e.touches[0]?.clientX ?? null
      }}
      onTouchEnd={(e) => {
        const awal = sentuh.current
        const akhir = e.changedTouches[0]?.clientX
        sentuh.current = null
        if (awal === null || akhir === undefined) return

        const geser = akhir - awal
        // Ambang 48 piksel memisahkan usapan dari guliran vertikal yang
        // sedikit menyerong; di bawah itu hampir selalu bukan maksud usapan.
        if (Math.abs(geser) > 48) ke(geser < 0 ? 1 : -1)
      }}
    >
      <div className="card-hard hard-dark relative overflow-hidden bg-arang-900">
        {slide.image_url ? (
          <img
            key={slide.id}
            src={slide.image_url}
            alt=""
            className="absolute inset-0 size-full animate-[skava-zoom_9s_ease-out_forwards] object-cover"
            loading={indeks === 0 ? 'eager' : 'lazy'}
          />
        ) : null}

        {/* Gradien menjamin tulisan tetap terbaca berapa pun terangnya foto
            yang diunggah admin. Tanpa ini, satu foto berlatar putih membuat
            judul putih di atasnya lenyap sama sekali. */}
        <div className="absolute inset-0 bg-gradient-to-r from-arang-900/90 via-arang-900/60 to-transparent" />

        <PromoLink
          promo={slide}
          className="relative flex min-h-[19rem] flex-col justify-end gap-3 p-6 sm:min-h-[23rem] sm:p-10"
        >
          <div className="max-w-xl space-y-3">
            <LabelSponsor promo={slide} className="block text-white" />
            <h2 className="display text-3xl leading-[1.05] text-white sm:text-5xl">{slide.title}</h2>
            {slide.subtitle ? (
              <p className="max-w-md text-sm text-white/80 sm:text-base">{slide.subtitle}</p>
            ) : null}
            {slide.cta_label ? (
              <span
                className={cn(
                  'mono-label mt-1 inline-flex items-center rounded-full px-5 py-2.5 text-[11px] transition-colors',
                  warna.tombol,
                )}
              >
                {slide.cta_label}
              </span>
            ) : null}
          </div>
        </PromoLink>

        {jumlah > 1 ? (
          <div className="absolute right-4 bottom-4 flex items-center gap-2 sm:right-6 sm:bottom-6">
            <TombolSlide label="Slide sebelumnya" onClick={() => ke(-1)}>
              <ChevronLeft className="size-4" />
            </TombolSlide>
            <TombolSlide
              label={berjalan ? 'Jeda putaran' : 'Lanjutkan putaran'}
              onClick={() => setBerjalan((n) => !n)}
            >
              {berjalan ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
            </TombolSlide>
            <TombolSlide label="Slide berikutnya" onClick={() => ke(1)}>
              <ChevronRight className="size-4" />
            </TombolSlide>
          </div>
        ) : null}
      </div>

      {jumlah > 1 ? (
        <div className="mt-3 flex items-center gap-2">
          {slides.map((item, nomor) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndeks(nomor)}
              aria-label={`Slide ${nomor + 1}: ${item.title}`}
              aria-current={nomor === indeks}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                nomor === indeks ? 'w-10 bg-ink' : 'w-4 bg-border hover:bg-ink-subtle',
              )}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}

function TombolSlide({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid size-9 place-items-center rounded-full border-2 border-white/30 bg-arang-900/60 text-white backdrop-blur transition-colors hover:border-white hover:bg-white hover:text-arang-900"
    >
      {children}
    </button>
  )
}

/**
 * PromoBanner menayangkan satu iklan mendatar pada slot yang diminta.
 *
 * Hanya satu yang ditampilkan meski ada beberapa yang aktif: dua banner
 * bertumpuk di satu tempat membuat keduanya terabaikan. Yang dipilih adalah
 * urutan pertama, sehingga admin menentukannya lewat kolom urutan.
 */
export function PromoBanner({ slot, className }: { slot: PromoSlot; className?: string }) {
  const { data } = usePromos(slot)
  const banner = data?.promos?.[0]
  const dicatat = useRef(false)

  useEffect(() => {
    if (!banner || dicatat.current) return
    dicatat.current = true
    lacakPromo(banner.id, 'view')
  }, [banner])

  if (!banner) return null

  const warna = aksen[banner.accent] ?? aksen.hijau

  return (
    <PromoLink
      promo={banner}
      className={cn(
        'card-hard group relative flex items-center gap-4 p-4 transition-transform hover:-translate-y-0.5 sm:gap-6 sm:p-5',
        warna.blok,
        className,
      )}
    >
      {banner.image_url ? (
        <img
          src={banner.image_url}
          alt=""
          className="hidden size-20 shrink-0 rounded-lg object-cover sm:block"
          loading="lazy"
        />
      ) : (
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-black/10">
          <Megaphone className="size-5" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <LabelSponsor promo={banner} className="block" />
        <p className="truncate text-base font-extrabold tracking-tight sm:text-lg">{banner.title}</p>
        {banner.subtitle ? (
          <p className="mt-0.5 line-clamp-2 text-sm opacity-80">{banner.subtitle}</p>
        ) : null}
      </div>

      {banner.cta_label ? (
        <span
          className={cn(
            'mono-label hidden shrink-0 rounded-full px-4 py-2 text-[11px] transition-colors sm:inline-flex',
            warna.tombol,
          )}
        >
          {banner.cta_label}
        </span>
      ) : null}
    </PromoLink>
  )
}
